import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Grid, Box, Typography, Button, Card, Tabs, Tab } from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import * as Yup from 'yup';
import { toast } from '../components/toast';

import { useLoadingState } from '../hooks/useLoadingState';
import DeleteConfirmationDialog from '../components/common/DeleteConfirmationDialog';
import {
  TeamsStatsCards,
  TeamsFilters,
  TeamsTable,
  TeamForm,
  AssignmentFilters,
  AssignmentTable,
  AssignmentDialog,
  AssignmentStatsCards,
} from '../components/teams';
import {
  getTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  getAllEmployees,
  getClients,
  assignClientToEmployee,
  unassignClientFromEmployee,
} from '../services/api';

function TeamsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({
    total_teams: 0,
    total_employees: 0,
    total_team_leads: 0,
    total_supervisors: 0,
  });

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [teamsPagination, setTeamsPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    page_size: 10,
    has_next: false,
    has_previous: false,
  });

  const [assignmentsPage, setAssignmentsPage] = useState(1);
  const [assignmentsRowsPerPage, setAssignmentsRowsPerPage] = useState(10);
  const [assignmentsPagination, setAssignmentsPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    page_size: 10,
    has_next: false,
    has_previous: false,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [assignmentsSearchTerm, setAssignmentsSearchTerm] = useState('');

  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });

  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedTeam, setSelectedTeam] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    team_lead_id: '',
    supervisor_id: '',
    team_member_ids: [],
  });
  const [formErrors, setFormErrors] = useState({});

  const [activeSection, setActiveSection] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam === 'assignments' ? 'assignments' : 'teams';
  });
  const [clientAssignments, setClientAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [assignmentStats, setAssignmentStats] = useState({
    total_assignments: 0,
    active_assignments: 0,
    unassigned_clients: 0,
    total_clients: 0,
  });

  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [assignmentDialogMode, setAssignmentDialogMode] = useState('add'); // 'add', 'edit'
  const [currentAssignmentClient, setCurrentAssignmentClient] = useState(null);
  const [currentAssignment, setCurrentAssignment] = useState(null); // For edit mode
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [unassignedClients, setUnassignedClients] = useState([]);
  const [unassignConfirmDialog, setUnassignConfirmDialog] = useState(false);
  const [assignmentToUnassign, setAssignmentToUnassign] = useState(null);

  const teamValidationSchema = Yup.object().shape({
    name: Yup.string().required('Team name is required'),
    description: Yup.string(),
    team_lead_id: Yup.number().nullable(),
    supervisor_id: Yup.number().nullable(),
    team_member_ids: Yup.array().of(Yup.number()),
  });

  const handleOpenDialog = (mode, team = null) => {
    setDialogMode(mode);
    setSelectedTeam(team);

    if (team) {
      setFormData({
        name: team.name || '',
        description: team.description || '',
        team_lead_id: team.team_lead?.id || '',
        supervisor_id: team.supervisor?.id || '',
        team_member_ids: team.team_members ? team.team_members.map((member) => member.id) : [],
      });
    } else {
      setFormData({
        name: '',
        description: '',
        team_lead_id: '',
        supervisor_id: '',
        team_member_ids: [],
      });
    }

    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTeam(null);
    setFormData({
      name: '',
      description: '',
      team_lead_id: '',
      supervisor_id: '',
      team_member_ids: [],
    });
    setFormErrors({});
  };

  const loadTeams = async (pageNum = page, search = searchTerm) => {
    try {
      const response = await getTeams(pageNum, rowsPerPage, search);
      if (response.data.success) {
        setTeams(response.data.data || []);
        if (response.data.pagination) {
          setTeamsPagination(response.data.pagination);
        }
        if (response.data.stats) {
          setStats((prevStats) => ({
            ...prevStats,
            total_teams: response.data.stats.total_teams,
            total_active: response.data.stats.total_active,
          }));
        }
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load teams.');
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await getAllEmployees();
      if (response.data.results) {
        setEmployees(response.data.results);
        setStats((prev) => ({
          ...prev,
          total_employees: response.data.results.length,
        }));
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  useEffect(() => {
    withLoading(async () => {
      if (activeSection === 'teams') {
        await Promise.all([loadTeams(1, ''), loadEmployees()]);
      } else {
        await Promise.all([loadClientAssignments(1, ''), loadEmployees()]);
      }
    });
  }, [activeSection, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeSection) {
      setActiveSection(tabParam === 'assignments' ? 'assignments' : 'teams');
    }
  }, [searchParams, activeSection]);

  // const changeTab = (tabName) => {
  //   if (tabName === 'teams' || tabName === 'assignments') {
  //     setActiveSection(tabName);
  //     setSearchParams((prev) => {
  //       const newParams = new URLSearchParams(prev);
  //       newParams.set('tab', tabName);
  //       return newParams;
  //     });
  //   }
  // };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await teamValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});

      const submitData = {
        name: formData.name,
        description: formData.description,
        team_lead_id: formData.team_lead_id || null,
        supervisor_id: formData.supervisor_id || null,
        team_member_ids: formData.team_member_ids || [],
      };

      let response;
      if (dialogMode === 'add') {
        response = await createTeam(submitData);
      } else if (dialogMode === 'edit') {
        response = await updateTeam(selectedTeam.id, submitData);
      }

      if (response.data.success) {
        handleCloseDialog();
        await loadTeams(page, searchTerm);

        if (dialogMode === 'add') {
          toast.success('Team created successfully!');
        } else if (dialogMode === 'edit') {
          toast.success('Team updated successfully!');
        }
      }
    } catch (error) {
      if (error.name === 'ValidationError') {
        const errors = {};
        error.inner.forEach((err) => {
          errors[err.path] = err.message;
        });
        setFormErrors(errors);
        toast.error('Please fix the form errors and try again.');
      } else {
        console.error('Error submitting team:', error);
        toast.error(dialogMode === 'add' ? 'Failed to create team.' : 'Failed to update team.');
      }
    }
  };

  const handleClearFilters = async () => {
    setSearchTerm('');
    setAssignmentsSearchTerm('');
    if (activeSection === 'teams') {
      setPage(1);
      await loadTeams(1, '');
    } else {
      setAssignmentsPage(1);
      await loadClientAssignments(1, '');
    }
  };

  const handleClearAssignmentFilters = async () => {
    setAssignmentsSearchTerm('');
    setAssignmentsPage(1);
    await loadClientAssignments(1, '');
  };

  const handleDeleteClick = (team) => {
    setSelectedTeam(team);
    setOpenDeleteDialog(true);
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedTeam(null);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteTeam(selectedTeam.id);
      if (response.data.success) {
        setOpenDeleteDialog(false);
        setSelectedTeam(null);
        await loadTeams(page, searchTerm);
        toast.success('Team deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team.');
    }
  };

  const loadClientAssignments = async (pageNum = assignmentsPage, search = '') => {
    try {
      const clientsResponse = await getClients(pageNum, assignmentsRowsPerPage, search);
      if (clientsResponse.data.status === 'success') {
        setClients(clientsResponse.data.results);
        if (clientsResponse.data.pagination) {
          setAssignmentsPagination(clientsResponse.data.pagination);
        }
        setAssignmentStats({
          total_assignments: clientsResponse.data.stats.assigned_clients,
          active_assignments: clientsResponse.data.stats.assigned_clients,
          unassigned_clients: clientsResponse.data.stats.unassigned_clients,
          total_clients: clientsResponse.data.stats.total_clients,
        });

        const assignedClients = clientsResponse.data.results.filter((client) => client.assigned_employee);
        setClientAssignments(assignedClients);
      }
    } catch (error) {
      console.error('Error loading client assignments:', error);
      toast.error('Failed to load client assignments.');
    }
  };

  const handleAssignmentDialogOpen = async (assignmentOrClient = null, mode = 'add') => {
    setAssignmentDialogMode(mode);

    if (mode === 'add') {
      const unassignedClientsList = clients.filter((c) => !c.assigned_employee);
      setUnassignedClients(unassignedClientsList);
      setCurrentAssignmentClient(null);
      setCurrentAssignment(null);
      setSelectedEmployee(null);
    } else {
      console.log('Edit mode - assignmentOrClient:', assignmentOrClient);

      setCurrentAssignment(assignmentOrClient);
      setCurrentAssignmentClient(assignmentOrClient);

      if (assignmentOrClient?.assigned_employee) {
        setSelectedEmployee(assignmentOrClient.assigned_employee);
      }
    }

    setAssignmentDialog(true);
  };

  const handleAssignmentDialogClose = () => {
    setAssignmentDialog(false);
    setCurrentAssignmentClient(null);
    setCurrentAssignment(null);
    setSelectedEmployee(null);
  };

  const handleAssignClient = async () => {
    if (!selectedEmployee || !currentAssignmentClient) {
      console.log('Early return - missing required data');
      return;
    }

    try {
      const assignmentId = assignmentDialogMode === 'edit' ? currentAssignment?.assignment_id : null;

      await assignClientToEmployee(currentAssignmentClient.id, selectedEmployee.id, assignmentId);
      await loadClientAssignments(assignmentsPage, assignmentsSearchTerm);
      handleAssignmentDialogClose();

      if (assignmentDialogMode === 'edit') {
        toast.success('Assignment updated successfully!');
      } else {
        toast.success('Client assigned successfully!');
      }
    } catch (error) {
      console.error('Failed to assign client:', error);

      if (error.response?.data?.error && error.response.data.error.includes('already has an active assignment')) {
        toast.error(error.response.data.error, {
          autoClose: 8000,
        });
      } else {
        toast.error(assignmentDialogMode === 'edit' ? 'Failed to update assignment.' : 'Failed to assign client.');
      }
    }
  };

  const handleUnassignConfirm = async () => {
    if (!assignmentToUnassign) return;

    try {
      await unassignClientFromEmployee(assignmentToUnassign.id);
      await loadClientAssignments(assignmentsPage, assignmentsSearchTerm);
      setUnassignConfirmDialog(false);
      setAssignmentToUnassign(null);
      toast.success('Client unassigned successfully!');
    } catch (error) {
      console.error('Failed to unassign client:', error);
      toast.error('Failed to unassign client.');
    }
  };

  const handleUnassignClick = (assignment) => {
    setAssignmentToUnassign(assignment);
    setUnassignConfirmDialog(true);
  };

  const handleUnassignCancel = () => {
    setUnassignConfirmDialog(false);
    setAssignmentToUnassign(null);
  };

  const handleTeamsPageChange = async (event, newPage) => {
    setPage(newPage);
    await loadTeams(newPage, searchTerm);
  };

  const handleTeamsRowsPerPageChange = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    await loadTeams(1, searchTerm);
  };

  const handleAssignmentsPageChange = async (event, newPage) => {
    setAssignmentsPage(newPage);
    await loadClientAssignments(newPage, assignmentsSearchTerm);
  };

  const handleAssignmentsRowsPerPageChange = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setAssignmentsRowsPerPage(newRowsPerPage);
    setAssignmentsPage(1);
    await loadClientAssignments(1, assignmentsSearchTerm);
  };

  const handleSearchChange = (event) => {
    const newSearch = event.target.value;
    setSearchTerm(newSearch);

    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(async () => {
      setPage(1);
      await loadTeams(1, newSearch);
    }, 300);
  };

  const handleAssignmentsSearchChange = (event) => {
    const newSearch = event.target.value;
    setAssignmentsSearchTerm(newSearch);

    clearTimeout(window.assignmentsSearchTimeout);
    window.assignmentsSearchTimeout = setTimeout(async () => {
      setAssignmentsPage(1);
      await loadClientAssignments(1, newSearch);
    }, 300);
  };

  const filteredTeams = teams;

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Team & Assignment Management</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" gutterBottom>
              Team & Assignment Management
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeSection}
                onChange={(event, newValue) => {
                  setActiveSection(newValue);
                  setSearchParams((prev) => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('tab', newValue);
                    return newParams;
                  });
                }}
                aria-label="team management tabs"
              >
                <Tab label="Teams Management" value="teams" icon={<GroupIcon />} iconPosition="start" />
                <Tab label="Client Assignments" value="assignments" icon={<AssignmentIcon />} iconPosition="start" />
              </Tabs>
            </Box>
          </Card>
        </Grid>

        {activeSection === 'teams' && (
          <>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" gutterBottom>
                  Teams Management
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                  Add Team
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TeamsStatsCards stats={stats} isLoading={isLoading} />
            </Grid>

            <Grid item xs={12}>
              <TeamsFilters
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                onClearFilters={handleClearFilters}
              />
            </Grid>

            <Grid item xs={12}>
              <TeamsTable
                teams={filteredTeams}
                isLoading={isLoading}
                page={teamsPagination.current_page}
                rowsPerPage={rowsPerPage}
                totalCount={teamsPagination.total_count}
                onPageChange={handleTeamsPageChange}
                onRowsPerPageChange={handleTeamsRowsPerPageChange}
                onViewTeam={(team) => handleOpenDialog('view', team)}
                onEditTeam={(team) => handleOpenDialog('edit', team)}
                onDeleteTeam={handleDeleteClick}
              />
            </Grid>
          </>
        )}

        {activeSection === 'assignments' && (
          <>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" gutterBottom>
                  Client Assignments
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => handleAssignmentDialogOpen(null, 'add')}
                >
                  New Assignment
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <AssignmentStatsCards stats={assignmentStats} isLoading={isLoading} />
            </Grid>

            <Grid item xs={12}>
              <AssignmentFilters
                searchTerm={assignmentsSearchTerm}
                onSearchChange={handleAssignmentsSearchChange}
                onClearFilters={handleClearAssignmentFilters}
              />
            </Grid>

            <Grid item xs={12}>
              <AssignmentTable
                assignments={clientAssignments}
                isLoading={isLoading}
                page={assignmentsPagination.current_page}
                rowsPerPage={assignmentsRowsPerPage}
                totalCount={assignmentsPagination.total_count}
                onPageChange={handleAssignmentsPageChange}
                onRowsPerPageChange={handleAssignmentsRowsPerPageChange}
                onEditAssignment={(assignment) => handleAssignmentDialogOpen(assignment, 'edit')}
                onUnassignClient={handleUnassignClick}
              />
            </Grid>
          </>
        )}
      </Grid>

      <TeamForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        employees={employees}
        selectedTeam={selectedTeam}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleFormChange}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete the team"
        itemName={selectedTeam?.name}
        itemToDelete={selectedTeam}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete"
      />

      <AssignmentDialog
        open={assignmentDialog}
        mode={assignmentDialogMode}
        currentAssignmentClient={currentAssignmentClient}
        selectedEmployee={selectedEmployee}
        employees={employees}
        unassignedClients={unassignedClients}
        onClose={handleAssignmentDialogClose}
        onSubmit={handleAssignClient}
        onClientChange={setCurrentAssignmentClient}
        onEmployeeChange={setSelectedEmployee}
      />

      <DeleteConfirmationDialog
        open={unassignConfirmDialog}
        title="Confirm Unassignment"
        message="Are you sure you want to unassign"
        itemName={assignmentToUnassign?.practice_name}
        itemToDelete={assignmentToUnassign}
        onClose={handleUnassignCancel}
        onConfirm={handleUnassignConfirm}
        confirmButtonText="Unassign"
        showDeleteIcon={false}
      />
    </>
  );
}

export default TeamsPage;
